import xhr from 'xhr';
import { STUDENT_SCHOOL_FILTER_OPTIONS, STUDENT_SCHOOL_GROUPS } from 'utils/studentSchools';

/**
 * «Asignar colegio» en bloque. Endpoints en klazia-api: src/api/school-assignment.
 * Roles: admin, supervisor_admin y secretary_admin.
 */

export const ROLES = ['admin', 'supervisor_admin', 'secretary_admin'];

export const STATUS_LABEL = {
  registrado: 'Registrado',
  estudiando: 'Estudiando',
  promocion_pendiente: 'Promoción Pendiente',
  promovido: 'Promovido',
  graduado: 'Graduado',
  retirado: 'Retirado',
  desercion: 'Deserción',
  aplazado: 'Aplazado',
  no_aprobado: 'No Aprobado',
};

const SCHOOL_LABELS = Object.fromEntries(STUDENT_SCHOOL_FILTER_OPTIONS.map((o) => [o.value, o.label]));
export const schoolLabel = (value) => (value ? SCHOOL_LABELS[value] || value : 'Sin colegio');

/** Grupo de colegios que corresponde a un tipo de programa (solo para advertir si no coincide). */
export function expectedSchoolGroup(programTypeName) {
  const n = String(programTypeName || '').toLowerCase();
  if (!n) return null;
  if (n.includes('técnic') || n.includes('tecnic')) return 'Técnicos';
  if ((n.includes('semestralizado') || n.includes('acelerado para adultos')) && !n.includes('usa')) return 'Bachillerato Semestralizado';
  if (n.includes('anual') || n.includes('flexible') || n.includes('homeschooling') || n.includes('primaria')) return 'Bachillerato Anual';
  return null;
}

export const groupOfSchool = (value) => STUDENT_SCHOOL_GROUPS.find((g) => g.options.some((o) => o.value === value))?.label || null;

export const errorMessage = (e) => e?.response?.data?.error?.message || 'No se pudo completar la acción.';

function query(params) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v);
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export async function getAssignmentOptions() {
  const { data } = await xhr.get('/school-assignment/options');
  return data;
}

export async function getAssignmentStudents(params) {
  const { data } = await xhr.get(`/school-assignment/students${query(params)}`);
  return data;
}

export async function applySchool(body) {
  const { data } = await xhr.post('/school-assignment/apply', body);
  return data;
}

/* ---------------------------------------------------------------------------------------------- */
/* Importar desde el Excel del área                                                                */
/* ---------------------------------------------------------------------------------------------- */

const plain = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Lee lo pegado desde una pestaña de Google Sheets o Excel (columnas separadas por tabulador).
 * Busca la fila de encabezados con «Codigo» y «Colegio»; «Numero» (el primero, que es el del
 * estudiante) es opcional y sirve para detectar códigos mal digitados.
 * Devuelve { rows: [{ line, code, name, document }], skippedNoSchool } o { error }.
 */
export function parseSheetPaste(text) {
  const lines = String(text || '').replace(/\r/g, '').split('\n');
  const cells = lines.map((l) => l.split('\t'));
  const headerIdx = cells.findIndex((row) => row.some((c) => plain(c) === 'codigo') && row.some((c) => plain(c) === 'colegio'));
  if (headerIdx < 0) {
    return { error: 'No encontré los encabezados «Codigo» y «Colegio». Copia la pestaña incluyendo la fila de encabezados.' };
  }
  const header = cells[headerIdx].map(plain);
  const codeCol = header.indexOf('codigo');
  const schoolCol = header.indexOf('colegio');
  const docCol = header.findIndex((h) => h === 'numero' || h === 'documento' || h === 'numero de documento');
  const rows = [];
  let skippedNoSchool = 0;
  cells.slice(headerIdx + 1).forEach((row, i) => {
    const code = String(row[codeCol] || '').replace(/\D/g, '');
    if (!code) return;
    const name = String(row[schoolCol] || '').trim();
    if (!name) { skippedNoSchool += 1; return; }
    rows.push({ line: headerIdx + i + 2, code, name, document: docCol >= 0 ? String(row[docCol] || '').trim() : '' });
  });
  if (!rows.length) return { error: 'No encontré filas con código y colegio debajo de los encabezados.' };
  return { rows, skippedNoSchool, hasDocument: docCol >= 0 };
}

// Nombres del Excel que se reconocen solos. «Instituto Iberoamericano» a secas puede ser Anual o CLEI:
// se deja sin sugerir para que el área lo elija.
const SCHOOL_ALIASES = [
  [/andes/, 'los andes'],
  [/^colegio iberoamericano$/, 'colegio iberoamericano'],
  [/tecnico/, 'instituto tecnico iberoamericano'],
  [/clei/, 'instituto iberoamerica clei'],
  [/iberoamericano anual/, 'iberoamericano'],
  [/inecol/, 'inecol'],
  [/edu?c?tec/, 'eductec'],
  [/fundetec/, 'fundetec'],
  [/coglobal/, 'coglobal'],
  [/klazia.*adult/, 'klazia international school adultos'],
  [/klazia/, 'klazia international school'],
];

/** Colegio de la plataforma que corresponde a un nombre del Excel, o '' si hay que elegirlo. */
export function suggestSchool(name) {
  const n = plain(name);
  const exact = STUDENT_SCHOOL_FILTER_OPTIONS.find((o) => plain(o.label) === n || plain(o.value) === n);
  if (exact && exact.value !== 'iberoamericano') return exact.value;
  const hit = SCHOOL_ALIASES.find(([re]) => re.test(n));
  return hit ? hit[1] : '';
}

export async function previewImport(rows) {
  const { data } = await xhr.post('/school-assignment/import/preview', { rows });
  return data;
}

export async function applyImport(rows, expectedChanges) {
  const { data } = await xhr.post('/school-assignment/import/apply', { rows, expectedChanges });
  return data;
}
